import { DeleteTreeOverlay } from '@fs/zion-group-management'
import { ContentDelete, NoticeWarning } from '@fs/zion-icon'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { Button, Card, colors, H3, Paragraph, Row, Separator, Skeleton, useOverlay } from '@fs/zion-ui'
import React from 'react'
import { useHistory } from '@fs/zion-router'
import { useEvent } from '@fs/zion-frontend-friends'
import { useTranslation } from 'react-i18next'
import { useTreeGroupDetailsContext } from '../providers/TreeGroupDetailsProvider'
import { useGedcomData } from '../../providers/GedcomDataProviderSmart'
import { usePrivatePersonCountContext } from '../../providers/PrivatePersonCountProvider'

const DELETE_TREE_PERSON_COUNT_THRESHOLD = 100_000
export default function DeleteTree(): JSX.Element {
  const [t] = useTranslation()
  const { privatePersonCount } = usePrivatePersonCountContext()
  return (
    <div>
      <H3 size="H6">{t('group-management.delete-researcher-group.action')}</H3>

      <Separator size="nano" />
      {!privatePersonCount && (
        <>
          <Skeleton.Paragraph maxWidth={200} />
          <Separator size="nano" />
          {/* eslint-disable-next-line test-selectors/button -- NA */}
          <Skeleton.Button />
        </>
      )}
      {!!privatePersonCount && privatePersonCount > DELETE_TREE_PERSON_COUNT_THRESHOLD && <ContactSupportContent />}
      {!!privatePersonCount && privatePersonCount <= DELETE_TREE_PERSON_COUNT_THRESHOLD && <DeleteTreeContent />}
    </div>
  )
}

function DeleteTreeContent(): JSX.Element {
  const [t] = useTranslation()
  const { group } = useTreeGroupDetailsContext()
  const { getAndSetGroups } = useGroupsContext()
  const deleteTreeOverlay = useOverlay()
  const { gedcomData } = useGedcomData()
  const history = useHistory()
  const onDeleteTree = useEvent(() => {
    getAndSetGroups?.(true)
    history.push('/research-tree')
  })

  return (
    <div>
      <Paragraph size="sm">{t('group-management.delete-tree.description')}</Paragraph>

      <Separator size="nano" />

      <Card backgroundColor={colors.gray03} edges="none" margins="xs">
        <Row alignY="middle" gutters="xs">
          <NoticeWarning size="xs" color={colors.yellow50} />
          <Paragraph size="sm">{t('group-management.delete-tree.warning')}</Paragraph>
        </Row>
      </Card>

      <Separator size="nano" />
      <Button data-testid="delete-tree-button" Icon={ContentDelete} onClick={deleteTreeOverlay.handleClick}>
        {t('group-management.delete-tree.action')}
      </Button>

      {(deleteTreeOverlay.isOpen || deleteTreeOverlay.transitioning) && (
        <DeleteTreeOverlay
          overlay={deleteTreeOverlay}
          groupId={group.id}
          fileName={gedcomData?.fileName}
          onDeleteTree={onDeleteTree}
        />
      )}
    </div>
  )
}

function ContactSupportContent(): JSX.Element {
  const [t] = useTranslation()

  return (
    <div>
      <Paragraph size="sm">
        {t('group-management.no-delete-possible.description', { number: DELETE_TREE_PERSON_COUNT_THRESHOLD })}
      </Paragraph>
      <Separator size="nano" />
      <Button
        data-testid="contact-support-button"
        to="https://www.familysearch.org/fieldops/familysearch-support-contact-us"
      >
        {t('group-management.contact-support.action')}
      </Button>
    </div>
  )
}
